import * as Yup from 'yup';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Container, Typography, Box, Stack, IconButton, InputAdornment, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// auth action
import { loginUser } from '../store/actions/auth';

// components
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import { FormProvider, RHFTextField } from '../components/hook-form';

// ----------------------------------------------------------------------

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ brandColor }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f5f5f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-30%',
    left: '-10%',
    width: '60%',
    height: '70%',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${brandColor}12 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-20%',
    right: '-10%',
    width: '55%',
    height: '65%',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${brandColor}14 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
}));

const GlassCard = styled('div')({
  width: '100%',
  maxWidth: 420,
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(32px)',
  WebkitBackdropFilter: 'blur(32px)',
  borderRadius: 24,
  border: '1px solid rgba(255,255,255,0.4)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)',
  padding: '48px 44px',
  animation: `${fadeUp} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both`,
});

const BrandTextField = styled(RHFTextField, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ brandColor }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
    borderRadius: 12,
    background: '#f5f5f7',
    border: '1.5px solid #d2d2d7',
    '& fieldset': { border: '1.5px solid transparent' },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: `0 0 0 4px ${brandColor}1A`,
      '& fieldset': { borderColor: brandColor },
    },
  },
}));

const SubmitButton = styled(LoadingButton, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ brandColor }) => ({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  color: 'white',
  fontWeight: 600,
  background: brandColor,
  '&:hover': {
    background: brandColor,
    filter: 'brightness(0.9)',
  },
  boxShadow: 'none',
  '&.Mui-disabled': {
    color: 'white',
    background: '#bcbcc5',
  },
}));

// ----------------------------------------------------------------------

export default function WhitelabelLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);

  const { isLoading, isAuthenticated } = useSelector((state) => state.auth);

  // Retrieve bootstrapped app branding from sessionStorage
  const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
  const app = bootstrappedApp ? JSON.parse(bootstrappedApp) : null;
  const brandColor = app?.branding?.themeColor || '#1d8dff';
  const logoUrl = app?.branding?.logoUrl;
  const appName = app?.name || 'Admin Panel';

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    dispatch(loginUser(data));
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <Page title={`Sign In | ${appName}`}>
      <RootStyle brandColor={brandColor}>
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
          <GlassCard>
            {logoUrl ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt={appName}
                  sx={{
                    maxHeight: 60,
                    maxWidth: '80%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: `${brandColor}15`,
                  color: brandColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: 700,
                  mb: 3,
                  mx: 'auto',
                  border: `2px solid ${brandColor}`,
                }}
              >
                {appName.charAt(0).toUpperCase()}
              </Box>
            )}

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#1d1d1f',
                lineHeight: 1.2,
                mb: 4,
                textAlign: 'center',
              }}
            >
              Sign In to {appName}
            </Typography>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2.5}>
                <BrandTextField name="email" label="Email Address" brandColor={brandColor} />

                <BrandTextField
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  brandColor={brandColor}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Link
                    variant="subtitle2"
                    underline="hover"
                    sx={{ cursor: 'pointer', color: brandColor, fontSize: '0.875rem' }}
                    onClick={() => navigate('/password-reset')}
                  >
                    Forgot password?
                  </Link>
                </Stack>

                <SubmitButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting || isLoading}
                  brandColor={brandColor}
                  sx={{ mt: 2 }}
                >
                  Sign in
                </SubmitButton>
              </Stack>
            </FormProvider>
          </GlassCard>
        </Container>
      </RootStyle>
    </Page>
  );
}
